import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/core/api.service';
import { LoggerService } from 'src/app/core/logger.service';
import { AuthService } from '../../auth/services/auth.service';
import * as fromRoot from 'src/app/reducers';
import { BlendEvent, User } from 'src/app/shared/models';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ChargebeeService } from '../../shared/services/chargebee.service';
import { BlendService } from '../../shared/services/blend.service';
import { SkuService } from '../../shared/services/sku.service';
import { cAccountInfo } from 'src/app/shared/models/api/chargbeeprofile-api.model';

@Component({
  selector: 'app-cancel-subscription',
  templateUrl: './cancel-subscription.component.html',
  styleUrls: ['./cancel-subscription.component.scss']
})
export class CancelSubscriptionComponent implements OnInit, OnDestroy {

  message: string;
  countdown = 5;
  redirectTarget = '';
  user: User;
  sub: Subscription;
  chargebeeSubscription: Subscription;
  chargebeeProfile: cAccountInfo;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private log: LoggerService,
    private router: Router,
    private auth: AuthService,
    private store: Store<fromRoot.State>,
    private chargebeeService: ChargebeeService,
    private blendService: BlendService,
    private skuService: SkuService
  ) { }

  ngOnInit(): void {
    this.sub = this.store
      .select(fromRoot.getAuthState)
      .subscribe((data) => {
        if (!data.authenticated) {
          this.user = new User();
          return;
        }
        if (data.user) {
          this.user = data.user;
        }
      });

    this.chargebeeSubscription = this.chargebeeService.getSubscription().subscribe(data => {
      if (data.isLoaded) {
        this.chargebeeProfile = data.cprofile;
      }
    });

    this.cancelSubscription();
  }

  private async cancelSubscription() {
    const qp = this.route.snapshot.queryParams;
    const subscriptionId = qp['sub_id'];
    if (!subscriptionId) {
      this.showMessageAndRedirect('Missing subscription ID', '/settings/billing');
      return;
    }

    const payload: any = { sub_id: subscriptionId };

    if (qp['reason'])        payload.reason = qp['reason'];
    if (qp['reason_other'])  payload.reason_other = qp['reason_other'];
    if (qp['feedback'])      payload.feedback = qp['feedback'];
    if (qp['competition'])   payload.competition = qp['competition'];

    try {
      const res = await this.apiService.execute('cancelchargebeesubscription', payload);

      if (res?.success) {
        // Track the account deletion event
        await this.trackAccountDeletion(payload);

        // Free and trial user accounts are directly marked as closed
        // So we log out the user and redirect to thank you page
        if (this.user.is_on_trial || !this.user.is_pro) {
          this.redirectToThankYouPage();
        } else {
          await this.chargebeeService.refreshSubscription();
          this.showMessageAndRedirect('Subscription cancelled successfully', '/files');
        }
      } else {
        this.showMessageAndRedirect('Failed to cancel subscription', '/settings/billing');
      }

    } catch (ex) {
      this.log.error(`Error cancelling subscription: ${JSON.stringify(ex, null, 4)}`);
      this.showMessageAndRedirect('An error occurred while cancelling subscription', '/settings/billing');
    }
  }

  private showMessageAndRedirect(msg: string, target: string) {
    this.message = msg;
    this.redirectTarget = target === '/files' ? 'Dashboard' : 'Billing Section';

    const timer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(timer);
        this.router.navigate([target]);
      }
    }, 1000);
  }

  private async redirectToThankYouPage() {
    await this.auth.logout();
    window.location.href = 'https://www.sync.com/support/?thank=you';
  }

  private async trackAccountDeletion(payload: any) {
    const seats = await this.skuService.getPanLimit();
    const plan = await this.skuService.getSku();

    this.blendService.track(BlendEvent.DELETE_ACCOUNT, {
      reason: payload.reason ?? 'User cancelled subscription',
      reason_other: payload.reason_other ?? 'N/A',
      feedback: payload.feedback ?? 'N/A',
      competition: payload.competition ?? 'N/A',
      selections: 'Chargebee flow 2025',
      plan,
      seats,
      signup_date: new Date(this.user.join_date).toISOString(),
      subscription_start_date: this.chargebeeProfile.subscription?.subscriptionStartDate
        ? new Date(this.chargebeeProfile.subscription.subscriptionStartDate).toISOString()
        : 'N/A',
      subscription_end_date: this.chargebeeProfile.subscription?.subscriptionEndDate
        ? new Date(this.chargebeeProfile.subscription.subscriptionEndDate).toISOString()
        : 'N/A',
    });
  }


  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.chargebeeSubscription) {
      this.chargebeeSubscription.unsubscribe();
    }
  }
}
